import type { DepartureTimeRow } from '../../services/departureTimeService';
import { pickTopDepartureWindow } from '../../components/charts/DepartureTimeChart';

/** ACS departure_time_bin labels as stored in the DB (aligned with DepartureTimeChart). */
const ACS_DEPARTURE_BINS = [
  '12:00 a.m. to 4:59 a.m.',
  '5:00 a.m. to 5:29 a.m.',
  '5:30 a.m. to 5:59 a.m.',
  '6:00 a.m. to 6:29 a.m.',
  '6:30 a.m. to 6:59 a.m.',
  '7:00 a.m. to 7:29 a.m.',
  '7:30 a.m. to 7:59 a.m.',
  '8:00 a.m. to 8:29 a.m.',
  '8:30 a.m. to 8:59 a.m.',
  '9:00 a.m. to 9:59 a.m.',
  '10:00 a.m. to 10:59 a.m.',
  '11:00 a.m. to 11:59 a.m.',
  '12:00 p.m. to 3:59 p.m.',
  '4:00 p.m. to 11:59 p.m.',
] as const;

const canonicalKey = (label: string | null | undefined) =>
  String(label || '')
    .replace(/^Total:\s*/i, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\./g, '');

const KNOWN_CANONICAL = new Set(ACS_DEPARTURE_BINS.map(canonicalKey));
const BEFORE_7AM = new Set(ACS_DEPARTURE_BINS.slice(0, 5).map(canonicalKey));
const AFTER_9AM = new Set(ACS_DEPARTURE_BINS.slice(9).map(canonicalKey));

function rowPercentOrEstimate(r: DepartureTimeRow): number | null {
  if (r.percent != null) {
    const p = Number(r.percent);
    return Number.isFinite(p) ? p : null;
  }
  const e = Number(r.estimate);
  return Number.isFinite(e) ? e : null;
}

function computePercentByBin(rows: DepartureTimeRow[]): {
  year: number | null;
  byBin: Map<string, number>;
  hasUnknownBin: boolean;
  denominatorValid: boolean;
} {
  if (!rows.length) {
    return { year: null, byBin: new Map(), hasUnknownBin: false, denominatorValid: false };
  }
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const latestYear = years[years.length - 1];
  const latestRows = rows.filter((r) => r.year === latestYear);
  const estimates: number[] = [];
  for (const r of latestRows) {
    const v = rowPercentOrEstimate(r);
    if (v === null) return { year: latestYear, byBin: new Map(), hasUnknownBin: true, denominatorValid: false };
    if (r.percent == null) estimates.push(v);
  }
  const total = estimates.length ? estimates.reduce((a, b) => a + b, 0) : 0;
  const byBin = new Map<string, number>();
  let hasUnknownBin = false;
  latestRows.forEach((r) => {
    const bin = canonicalKey(r.departure_time_bin);
    if (!KNOWN_CANONICAL.has(bin)) {
      hasUnknownBin = true;
      return;
    }
    const raw = rowPercentOrEstimate(r);
    if (raw === null) {
      hasUnknownBin = true;
      return;
    }
    const value = r.percent != null ? raw : total > 0 ? (raw / total) * 100 : null;
    if (value === null) {
      hasUnknownBin = true;
      return;
    }
    byBin.set(bin, (byBin.get(bin) || 0) + value);
  });
  const denominatorValid = !hasUnknownBin && byBin.size > 0 && (total > 0 || latestRows.every((r) => r.percent != null));
  return { year: latestYear, byBin, hasUnknownBin, denominatorValid };
}

function sumShares(byBin: Map<string, number>, keys: Set<string>): number {
  let sum = 0;
  for (const k of keys) {
    sum += byBin.get(k) ?? 0;
  }
  return sum;
}

export type DepartureNarrativeFacts = {
  latestYear: number | null;
  dominantDepartureWindow: string | null;
  dominantDepartureShare: number | null;
  before7amShare: number | null;
  after9amShare: number | null;
};

export function buildDepartureNarrativeFacts(rows: DepartureTimeRow[]): DepartureNarrativeFacts {
  if (!rows.length) {
    return {
      latestYear: null,
      dominantDepartureWindow: null,
      dominantDepartureShare: null,
      before7amShare: null,
      after9amShare: null,
    };
  }
  const { year, byBin, hasUnknownBin, denominatorValid } = computePercentByBin(rows);
  const window = pickTopDepartureWindow(rows);
  const max = byBin.size ? Math.max(...Array.from(byBin.values())) : -1;
  const dominantShare = Number.isFinite(max) && max >= 0 ? Number(max.toFixed(1)) : null;
  const canAggregate = !hasUnknownBin && denominatorValid;
  const before7 = canAggregate ? Number(sumShares(byBin, BEFORE_7AM).toFixed(1)) : null;
  const after9 = canAggregate ? Number(sumShares(byBin, AFTER_9AM).toFixed(1)) : null;
  return {
    latestYear: year,
    dominantDepartureWindow: window,
    dominantDepartureShare: dominantShare,
    before7amShare: before7,
    after9amShare: after9,
  };
}
